import React, { useState, useEffect } from 'react';
import { Drawer, Checkbox, Input, Space, Button, message, Spin, Empty } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import rbacService from '../../services/rbacService';

/**
 * Assign Roles Drawer Component
 * Allows assigning roles to a user
 */
export default function AssignRolesDrawer({ visible, user, onClose, onSuccess }) {
  const [roles, setRoles] = useState([]);
  const [assignedRoleIds, setAssignedRoleIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (visible && user) {
      fetchRoles();
      fetchUserRoles();
    }
  }, [visible, user]);

  const fetchUserRoles = async () => {
    if (!user?.id) return;
    try {
      const result = await rbacService.getUserRoles(user.id);
      if (result.success && Array.isArray(result.data)) {
        const ids = result.data.map((r) => (r.id != null ? String(r.id) : r.id)).filter(Boolean);
        setAssignedRoleIds(ids);
      } else {
        setAssignedRoleIds([]);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setAssignedRoleIds([]);
    }
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const result = await rbacService.getRoles();
      if (result.success) {
        // Ensure data is always an array
        const data = result.data;
        setRoles(Array.isArray(data) ? data : []);
      } else {
        message.error('Failed to load roles');
        setRoles([]);
      }
    } catch (error) {
      message.error('Error loading roles');
      console.error('Error fetching roles:', error);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRole = (roleId) => {
    const idStr = roleId != null ? String(roleId) : roleId;
    setAssignedRoleIds((prev) => {
      if (prev.includes(idStr)) {
        return prev.filter((id) => id !== idStr);
      } else {
        return [...prev, idStr];
      }
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredRoles();
    const allIds = filtered.map((r) => (r.id != null ? String(r.id) : r.id));
    const allSelected = allIds.every((id) => assignedRoleIds.includes(id));

    if (allSelected) {
      setAssignedRoleIds((prev) =>
        prev.filter((id) => !allIds.includes(id))
      );
    } else {
      setAssignedRoleIds((prev) => {
        const newIds = [...prev];
        allIds.forEach((id) => {
          if (!newIds.includes(id)) {
            newIds.push(id);
          }
        });
        return newIds;
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const roleIds = assignedRoleIds.map((id) => (Number.isFinite(Number(id)) ? Number(id) : id));
      const result = await rbacService.assignRolesToUser(user.id, {
        role_ids: roleIds,
      });
      if (result.success) {
        message.success('Roles assigned successfully');
        onSuccess?.();
        onClose();
      } else {
        message.error(result.message || 'Failed to assign roles');
      }
    } catch (error) {
      message.error('Error assigning roles');
      console.error('Error assigning roles:', error);
    } finally {
      setSaving(false);
    }
  };

  const getFilteredRoles = () => {
    if (!searchText.trim()) {
      return roles;
    }
    const lowerSearch = searchText.toLowerCase();
    return roles.filter(
      (r) =>
        r.name?.toLowerCase().includes(lowerSearch) ||
        r.description?.toLowerCase().includes(lowerSearch)
    );
  };

  const filteredRoles = getFilteredRoles();
  const allFilteredSelected =
    filteredRoles.length > 0 &&
    filteredRoles.every((r) => assignedRoleIds.includes(r.id != null ? String(r.id) : r.id));

  return (
    <Drawer
      title={`Assign Roles - ${user?.name}`}
      open={visible}
      onClose={onClose}
      width={600}
      destroyOnClose
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          paddingBottom: 24,
        },
      }}
      footer={
        <Space className="float-right">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            Save Roles
          </Button>
        </Space>
      }
    >
      <div className="flex flex-col flex-1 min-h-0">
        <div className="shrink-0 mb-4">
          <Input
            placeholder="Search roles..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </div>

        <div className="shrink-0 mb-4 flex justify-between items-center">
          <span className="text-sm text-gray-600">
            {assignedRoleIds.length} of {roles.length} roles selected
          </span>
          {filteredRoles.length > 0 && (
            <Button
              type="link"
              size="small"
              onClick={handleSelectAll}
            >
              {allFilteredSelected ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </div>

        <div className="flex-1 min-h-0 relative overflow-hidden">
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden">
            <Spin spinning={loading}>
              {filteredRoles.length === 0 ? (
                <Empty
                  description={
                    searchText
                      ? 'No roles found matching your search'
                      : 'No roles available'
                  }
                />
              ) : (
                <Space direction="vertical" className="w-full" size="middle">
                  {filteredRoles.map((role) => {
                    const isChecked = assignedRoleIds.includes(role.id != null ? String(role.id) : role.id);
                    return (
                      <div
                        key={role.id}
                        className={`p-3 border rounded-lg transition-colors ${
                          isChecked
                            ? 'border-primary bg-primary-muted'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleToggleRole(role.id)}
                          className="w-full"
                        >
                          <div className="ml-2">
                            <div className="font-medium text-gray-900">
                              {role.name}
                            </div>
                            {role.description && (
                              <div className="text-sm text-gray-600 mt-1">
                                {role.description}
                              </div>
                            )}
                          </div>
                        </Checkbox>
                      </div>
                    );
                  })}
                </Space>
              )}
            </Spin>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
